import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';

type Props = {
    page: number;
    totalPages: number;
    pageIndexes: number[];
    onPrev: () => void;
    onNext: () => void;
    onPageSelect: (page: number) => void;
    labels?: {
        prev?: string;
        next?: string;
    };
};

const NextIcon = (props: { className: string }) => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className={props.className}>
            <path fillRule="evenodd" clipRule="evenodd" d="M17.0323 2.46888C17.1022 2.53854 17.1576 2.62131 17.1954 2.71243C17.2332 2.80354 17.2527 2.90122 17.2527 2.99988C17.2527 3.09853 17.2332 3.19621 17.1954 3.28733C17.1576 3.37844 17.1022 3.46121 17.0323 3.53088L8.56184 11.9999L17.0323 20.4689C17.1732 20.6097 17.2523 20.8007 17.2523 20.9999C17.2523 21.199 17.1732 21.39 17.0323 21.5309C16.8915 21.6717 16.7005 21.7508 16.5013 21.7508C16.3022 21.7508 16.1112 21.6717 15.9703 21.5309L6.97034 12.5309C6.9005 12.4612 6.84508 12.3784 6.80727 12.2873C6.76946 12.1962 6.75 12.0985 6.75 11.9999C6.75 11.9012 6.76946 11.8035 6.80727 11.7124C6.84508 11.6213 6.9005 11.5385 6.97034 11.4689L15.9703 2.46888C16.04 2.39903 16.1228 2.34362 16.2139 2.30581C16.305 2.268 16.4027 2.24854 16.5013 2.24854C16.6 2.24854 16.6977 2.268 16.7888 2.30581C16.8799 2.34362 16.9627 2.39903 17.0323 2.46888Z" fill="black"/>
        </svg>
    );
};

export const SearchPagination: React.FC<Props> = ({
    page,
    totalPages,
    pageIndexes,
    onPrev,
    onNext,
    onPageSelect,
    labels,
}) => {
    const prev = labels.prev || 'Prev';
    const next = labels.next || 'Next';
    const WINDOW_SIZE = 6;
    const lastIndex = Math.max(0, totalPages - 1);
    const [windowStart, setWindowStart] = useState(0);

    useEffect(() => {
        setWindowStart(0);
    }, [totalPages]);

    const maxStart = useMemo(() => Math.max(0, lastIndex - WINDOW_SIZE), [lastIndex]);

    const windowEndExclusive = useMemo(() => {
        // показываем только середину, последнюю страницу рисуем отдельно
        return Math.min(windowStart + WINDOW_SIZE, lastIndex);
    }, [windowStart, WINDOW_SIZE, lastIndex]);

    const canShiftLeft = useMemo(() => windowStart > 0, [windowStart]);
    const canShiftRight = useMemo(() => windowEndExclusive < lastIndex, [windowEndExclusive, lastIndex]);

    const handleShiftLeft = () => {
        if (!canShiftLeft) {
            return;
        }
        setWindowStart(prev => {
            const nextStart = Math.max(0, prev - WINDOW_SIZE);
            // Выбираем первую страницу нового окна, чтобы авто-сдвиг не откатывал окно назад
            onPageSelect(nextStart + 1);
            return nextStart;
        });
    };

    const handleShiftRight = () => {
        if (!canShiftRight) {
            return;
        }
        setWindowStart(prev => {
            const nextStart = Math.min(maxStart, prev + WINDOW_SIZE);
            // Выбираем первую страницу нового окна, чтобы авто-сдвиг не откатывал окно назад
            onPageSelect(nextStart + 1);
            return nextStart;
        });
    };

    // Автосдвиг окна при смене активной страницы (Next/Prev/прямой выбор)
    useEffect(() => {
        const pageIndex = page - 1;
        // последняя страница всегда видна отдельной кнопкой
        if (pageIndex === lastIndex) {
            return;
        }
        // если активная левее текущего окна — подвинем окно влево
        if (pageIndex < windowStart) {
            const desired = pageIndex - (WINDOW_SIZE - 1);
            const clamped = Math.max(0, Math.min(desired, maxStart));
            if (clamped !== windowStart) {
                setWindowStart(clamped);
            }
            return;
        }
        // если активная правее текущего окна — подвинем окно вправо
        if (pageIndex >= windowEndExclusive) {
            const desired = pageIndex - (WINDOW_SIZE - 1);
            const clamped = Math.max(0, Math.min(desired, maxStart));
            if (clamped !== windowStart) {
                setWindowStart(clamped);
            }
        }
    }, [page, lastIndex, windowStart, windowEndExclusive, WINDOW_SIZE, maxStart]);
    return (
        <nav className="pagination" role="navigation">
            <button
                type="button"
                onClick={onPrev}
                disabled={page <= 1}
                className="pagination__pages__control-button pagination__pages__control-button_left"
            >
                {prev} <NextIcon className="pagination__pages__control-button-icon_left pagination__pages__control-button-icon" />
            </button>
            <div className="pagination__pages" role="tablist">
                {/* Левое многоточие (двигаться назад) */}
                {canShiftLeft && (
                    <button
                        type="button"
                        className={cn('pagination__pages__page-button', 'pagination__pages__page-button_ellipsis')}
                        onClick={handleShiftLeft}
                        aria-label="Show previous pages"
                    >
                        …
                    </button>
                )}
                {/* Окно страниц */}
                {Array.from({ length: Math.max(0, windowEndExclusive - windowStart) }, (_, k) => windowStart + k).map((idx) => {
                    const pageNumber = idx + 1;
                    return (
                        <button
                            key={idx}
                            type="button"
                            className={cn('pagination__pages__page-button', {
                                active: page === pageNumber,
                            })}
                            onClick={() => onPageSelect(pageNumber)}
                            role="tab"
                            aria-selected={page === pageNumber}
                            aria-label={`Go to page ${pageNumber}`}
                        >
                            {pageNumber}
                        </button>
                    );
                })}
                {/* Правое многоточие (двигаться вперёд) */}
                {canShiftRight && (
                    <button
                        type="button"
                        className={cn('pagination__pages__page-button', 'pagination__pages__page-button_ellipsis')}
                        onClick={handleShiftRight}
                        aria-label="Show next pages"
                    >
                        …
                    </button>
                )}
                {/* Последняя страница (если не попала в окно) */}
                {lastIndex > 0 && windowEndExclusive <= lastIndex && (
                    <button
                        type="button"
                        className={cn('pagination__pages__page-button', {
                            active: page === (lastIndex + 1),
                        })}
                        onClick={() => onPageSelect(lastIndex + 1)}
                        role="tab"
                        aria-selected={page === (lastIndex + 1)}
                        aria-label={`Go to page ${lastIndex + 1}`}
                    >
                        {lastIndex + 1}
                    </button>
                )}
            </div>
            <button
                type="button"
                onClick={onNext}
                disabled={page >= totalPages}
                className="pagination__pages__control-button"
            >
                {next} <NextIcon className="pagination__pages__control-button-icon" />
            </button>
        </nav>
    );
};
